import { Shell } from 'repluggable';
import { default as api } from './apis/api';
import {
  CompRef,
  PageRef,
  DocumentServicesObject,
  RouterRef,
  AppData as DmAppData,
  AppDataComponent,
} from '@wix/document-services-types';
import {
  DocumentServicesAPI,
  EditorPointer,
  EditorPointerAPI,
} from '@wix/editorx-core-api';
import {
  EditorPlatformHostIntegrationAPI,
  PluginInstallationParams,
  PluginsHostApiContext,
  WidgetPointer,
  ToolsEntryEventListener,
  InitAppOptions,
  InstallCallbacks,
  InstallOptions,
  SilentInstallAppsOptions,
  IntentInstallOptions,
  SilentInstallAppsConfig,
  SilentInstallAppsCallbacks,
  HostEditActionsDictionary,
  ExecutionMethodsDictionary,
} from '@wix/editor-platform-host-integration-apis';
import {
  AppData,
  ComponentRef,
  PageVariant,
  Rule,
} from '@wix/editor-platform-sdk-types';
import { initV2 } from './apis/bootstrap/initV2';
import {
  createWorkspaceApi,
  IWorkspaceApiAdapter,
} from './apis/utils/workspaceApiAdapter';
import {
  createExperimentsApi,
  IExperimentsApiAdapter,
} from './apis/utils/experimentsApiAdapter';
import { platformEvents } from './platformEvents';
import { reportBi } from './essentials';
import { PlatformApiMethods, PlatformContext } from './types/platformApi';
import { installApps } from './apis/services/unifiedComponents/installation/addApps';
import { initBuilderServices } from './apis/services/builderServices/init';
import {
  initIntentInstallServices,
  getIntentInstallServices,
  createIntentInstallAsyncHandlers,
  notifyIntentInstallDidntHappen,
} from './apis/services/intentInstallService';
import { initEditActionsServices } from './apis/services/editActionsServices';
import { initPluginsServices } from './apis/services/plugins/init';

// these variables are being initiated in the "init" function. init function is called only once, but createEditorPlatformHostIntegrationAPI
// can be called multiple times, so need to save and share them
let context: PlatformContext;
let manifestServices: ReturnType<typeof initBuilderServices>['manifest'];
let panelsServices: ReturnType<typeof initBuilderServices>['panels'];
let intentInstallServices: ReturnType<typeof initIntentInstallServices>;
let editActionsServices: ReturnType<typeof initEditActionsServices>;
let pluginsServices: ReturnType<typeof initPluginsServices>;

// new API is created for each host (can be multiple)
export function createEditorPlatformHostIntegrationAPI(
  shell: Shell,
): EditorPlatformHostIntegrationAPI {
  const documentServicesAPI = shell.getAPI(
    DocumentServicesAPI,
  ) as unknown as DocumentServicesObject;
  const editorPointerAPI = shell.getAPI(EditorPointerAPI);
  const workspaceAPI: IWorkspaceApiAdapter = createWorkspaceApi(shell);
  const experimentsAPI: IExperimentsApiAdapter = createExperimentsApi(shell);

  // needed for cases when intent install and isRunning function can be called before platform initialization
  let intentInstallServiceAsyncHandlers = createIntentInstallAsyncHandlers();

  return {
    init: (
      platformApiMethods: PlatformApiMethods,
      initAppOptions?: InitAppOptions,
      extraNamespaces?: string[],
    ) => {
      const {
        platformApiMethods: platformAPIMethods,
        manifestServices: _manifestServices,
        panelsServices: _panelsServices,
        intentInstallServices: _intentInstallServices,
        editActionsServices: _editActionsServices,
        pluginsServices: _pluginsServices,
      } = api.init(
        {
          workspaceAPI,
          documentServicesAPI,
          experimentsAPI,
        },
        platformApiMethods,
        editorPointerAPI,
        initAppOptions,
        extraNamespaces,
      );

      context = {
        platformApiMethods: platformAPIMethods,
        manifestServices: _manifestServices,
        intentInstallServices: _intentInstallServices,
        experimentsAPI,
      };
      manifestServices = _manifestServices;
      panelsServices = _panelsServices;
      intentInstallServices = _intentInstallServices;
      editActionsServices = _editActionsServices;
      pluginsServices = _pluginsServices;
    },
    initV2,
    getWorkerManager: () => api.getWorkerManager(),
    setEditorAPI: (namespace: string, platformAPI: any) =>
      api.setEditorAPI(documentServicesAPI, namespace, platformAPI),
    applications: {
      setAppPublicAPI: (apiName: string) =>
        api.applications.setAppPublicAPI(documentServicesAPI, apiName),
      createDependenciesDriver: (
        appDefinitionId: string,
        appVersion?: string,
      ) =>
        api.applications.createDependenciesDriver(
          documentServicesAPI,
          appDefinitionId,
          appVersion,
        ),
      unifiedComponents: {
        installApps: (
          appDefinitionIds: string[],
          installOptions: InstallOptions,
          callbacks?: InstallCallbacks,
        ) =>
          installApps(
            context,
            documentServicesAPI,
            experimentsAPI,
            appDefinitionIds,
            installOptions,
            callbacks,
          ),
      },
      install: (
        appDefinitionIds: string[],
        installOptions?: InstallOptions,
        callbacks?: InstallCallbacks,
      ) =>
        api.applications.install(
          context,
          documentServicesAPI,
          experimentsAPI,
          appDefinitionIds,
          installOptions,
          callbacks,
        ),
      fetchNames: (appDefIds: string[]) =>
        api.applications.fetchNames(appDefIds),
      silentInstall: (
        appsToInstallSilently: string[],
        options: SilentInstallAppsOptions,
        serviceConfig: SilentInstallAppsConfig,
        serviceCallbacks: Partial<SilentInstallAppsCallbacks>,
      ) =>
        api.applications.silentInstall(
          context,
          documentServicesAPI,
          experimentsAPI,
          appsToInstallSilently,
          options,
          serviceConfig,
          serviceCallbacks,
        ),
      intentInstall: async (options: IntentInstallOptions) => {
        // in studio intent install call doesn't know anything about platform initialization
        // so we want to make sure that intent install services are initialized before calling it

        intentInstallServiceAsyncHandlers = createIntentInstallAsyncHandlers();

        intentInstallServiceAsyncHandlers.setIsIntentInstallStarting(true);
        const awaitedIntentInstallServices = await getIntentInstallServices();

        const intentInstallResult = awaitedIntentInstallServices.intentInstall(
          context,
          options,
        );

        intentInstallServiceAsyncHandlers.intentInstallLaunched();
        intentInstallServiceAsyncHandlers.setIsIntentInstallStarting(false);

        return intentInstallResult;
      },

      getIsIntentInstallRunningAsync: async () => {
        if (intentInstallServiceAsyncHandlers.getIsIntentInstallStarting()) {
          await intentInstallServiceAsyncHandlers.waitForIntentInstallToStart();
        }

        return intentInstallServices?.isIntentInstallRunning() || false;
      },

      isIntentInstallRunning: () =>
        intentInstallServices?.isIntentInstallRunning() || false,
      isSilentInstallRunning: () =>
        api.applications.isSilentInstallRunning() ||
        intentInstallServices?.isIntentInstallRunningInSilentMode() ||
        false,
      notifyIntentInstallDidntHappen,
    },
    pages: {
      isPageEssential: (pageId: string) =>
        api.pages.isPageEssential(documentServicesAPI, pageId),
      isPageDuplicatable: (pageId: string) =>
        api.pages.isPageDuplicatable(documentServicesAPI, pageId),
    },
    components: {
      getComponentApp: (compRef: CompRef) =>
        api.components.getComponentApp(documentServicesAPI, compRef),
      handleEssentialsComponentsBeforeDelete: (compRefs: CompRef[]) =>
        api.components.handleEssentialsComponentsBeforeDelete(
          context,
          documentServicesAPI,
          compRefs,
        ),
      getPlatformizedComponentData: (componentRef: CompRef) =>
        api.components.getPlatformizedComponentData(
          documentServicesAPI,
          componentRef,
        ),
      isWidgetStretchable: (compRef: CompRef, appData?: DmAppData) =>
        api.components.isWidgetStretchable(
          documentServicesAPI,
          compRef,
          appData,
        ),
      getComponentIdentifiers: (compRef: CompRef, appData?: DmAppData) =>
        api.components.getComponentIdentifiers(
          documentServicesAPI,
          compRef,
          appData,
        ),
      isComponentEssential: (compRef: CompRef) =>
        api.components.isComponentEssentialByRef(documentServicesAPI, compRef),
    },
    sdk: {
      overrideImplementation: api.sdk.overrideImplementation,
    },
    pageReplace: {
      setReplacerPage: (
        pageRef: PageRef,
        replacerPageRef: PageRef,
        setAsActive: boolean,
      ) =>
        api.pageReplace.setReplacerPage(documentServicesAPI, {
          replacerPageRef,
          pageRef,
          setAsActive,
        }),
      openPageVariantsPanel: (
        pageTitle: string,
        variants: PageVariant[],
        pageManagingAppDefId: string,
        pageRef: PageRef,
      ) =>
        api.pageReplace.openPageVariantsPanel(documentServicesAPI, context, {
          pageTitle,
          variants,
          pageManagingAppDefId,
          pageRef,
        }),
      addVariantToPage: (
        pageRef: PageRef,
        variantPageRef: PageRef,
        rule: Rule,
        variantId: string,
      ) =>
        api.pageReplace.addVariantToPage(documentServicesAPI, {
          pageRef,
          variantPageRef,
          rule,
          variantId,
        }),
      getPageInfo: (pageRef: PageRef) =>
        api.pageReplace.getPageInfo(documentServicesAPI, pageRef),
      getPageVariations: (pageRef: PageRef) =>
        api.pageReplace.getPageVariations(documentServicesAPI, pageRef),
      isReplaceable: (appDefinitionId: string, pageRef: PageRef) =>
        api.pageReplace.isReplaceable(
          documentServicesAPI,
          appDefinitionId,
          pageRef,
        ),
      getPageAvailableVariants: (appDefinitionId: string, pageRef: PageRef) =>
        api.pageReplace.getPageAvailableVariants(
          documentServicesAPI,
          appDefinitionId,
          pageRef,
        ),
      getOriginalPageRef: (pageRef: PageRef) =>
        api.pageReplace.getOriginalPageRef(documentServicesAPI, pageRef),
      isReplacer: (pageRef: PageRef) =>
        api.pageReplace.isReplacer(documentServicesAPI, pageRef),
      isVariant: (pageRef: PageRef) =>
        api.pageReplace.isVariant(documentServicesAPI, pageRef),
      isReplacerActive: (pageRef: PageRef) =>
        api.pageReplace.isReplacerActive(documentServicesAPI, pageRef),
      isRouterExistForPage: (pageRef: PageRef) =>
        api.pageReplace.isRouterExistForPage(documentServicesAPI, pageRef),
      createRouter: (pageRef: PageRef) =>
        api.pageReplace.createRouter(documentServicesAPI, pageRef),
      connectPageToRouter: (routerRef: RouterRef, pageRef: PageRef) =>
        api.pageReplace.connectPageToRouter(
          documentServicesAPI,
          routerRef,
          pageRef,
        ),
      createBlankReplacerPage: (pageRef: PageRef) =>
        api.pageReplace.createBlankReplacerPage(
          context,
          documentServicesAPI,
          pageRef,
        ),
      openDeleteReplacerPageModal: (pageRef: PageRef) =>
        api.pageReplace.openDeleteReplacerPageModal(
          context,
          documentServicesAPI,
          pageRef,
        ),
    },
    viewState: {
      getViewState: (appData: AppData, componentRef: ComponentRef) => {
        return api.viewState.getViewState(
          documentServicesAPI,
          appData,
          componentRef,
        );
      },
      setViewState: (
        appData: AppData,
        componentRef: ComponentRef,
        viewState: string,
      ) => {
        api.viewState.setViewState(
          documentServicesAPI,
          appData,
          componentRef,
          viewState,
        );
      },
    },
    canvasOverlay: api.canvasOverlay,
    toolsEntry: {
      register: (listener: ToolsEntryEventListener) => {
        api.toolsEntry.toolsEntryStore.register(listener);
      },
      unregister: (listener: ToolsEntryEventListener) => {
        api.toolsEntry.toolsEntryStore.unregister(listener);
      },
    },
    platformEvents,
    slots: {
      /*
       TODO: refactor slots API, the following methods should be moved inside plugins:
      `getPluginInstallationParams`, `autoInstall`, `autoInstallWithNavigation`, `getAllPluginsInstallationParams`
      */
      getPluginInstallationParams: (
        appDefinitionId: string,
        components: AppDataComponent[],
      ) =>
        api.slots.getPluginInstallationParams(
          documentServicesAPI,
          appDefinitionId,
          components,
        ),
      autoInstall: (
        pluginInstallationParams: PluginInstallationParams,
        pluginsHostAPIContext: PluginsHostApiContext,
      ) =>
        api.slots.autoInstall.install(
          context,
          documentServicesAPI,
          pluginInstallationParams,
          pluginsHostAPIContext,
          experimentsAPI,
        ),
      autoInstallWithNavigation: (
        pluginInstallationParams: PluginInstallationParams,
        pluginsHostAPIContext: PluginsHostApiContext,
      ) => {
        return api.slots.autoInstall.installWithNavigation(
          context,
          documentServicesAPI,
          pluginInstallationParams,
          pluginsHostAPIContext,
          experimentsAPI,
        );
      },
      getAllPluginsInstallationParams: (
        appDefinitionId: string,
        components: AppDataComponent[],
      ) =>
        api.slots.getAllPluginsInstallationParams(
          documentServicesAPI,
          appDefinitionId,
          components,
        ),
      getSlotPlaceholderRefByContentRef: (compRef: CompRef) =>
        pluginsServices.slots.getSlotPlaceholderRefByContentRef(compRef),
      getWidgetHostRefBySlotRef: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetHostRefBySlotRef(compRef),
      getWidgetHostRefByWidgetRef: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetHostRefByWidgetRef(compRef),
      getWidgetSlot: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetSlot(compRef),
      getWidgetSlots: (widgetCompRef: CompRef) =>
        pluginsServices.slots.getWidgetSlots(widgetCompRef),
      getSlotPosition: (compRef: CompRef) =>
        pluginsServices.slots.getSlotPosition(compRef),
      getCompRefForSlotSelection: (compRef: CompRef) =>
        pluginsServices.slots.getCompRefForSlotSelection(compRef),
      getSlotInBlocksAppEditor: (slotCompRef: CompRef) =>
        pluginsServices.slots.getSlotInBlocksAppEditor(slotCompRef),
      getAvailableWidgetSlotsToMoveContent: (compRef: CompRef) =>
        pluginsServices.slots.getAvailableWidgetSlotsToMoveContent(compRef),
      getWidgetSlotsToMoveContent: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetSlotsToMoveContent(compRef),
      hasWidgetSlot: (widgetCompRef: CompRef) =>
        pluginsServices.slots.hasWidgetSlot(widgetCompRef),
      moveToSlot: (
        fromSlotComponentRef: CompRef,
        toSlotComponentRef: CompRef,
        pluginsHostAPIContext: PluginsHostApiContext,
      ) =>
        pluginsServices.slots.moveToSlot(
          context,
          fromSlotComponentRef,
          toSlotComponentRef,
          pluginsHostAPIContext,
        ),
      highlightSlot: (compRef: CompRef) =>
        pluginsServices.slots.highlightSlot(compRef),
      unhighlightSlot: (compRef: CompRef) =>
        pluginsServices.slots.unhighlightSlot(compRef),
      isSlotContentMovable: (compRef: CompRef) =>
        pluginsServices.slots.isSlotContentMovable(compRef),
      isSlotContentPotentialMovable: (compRef: CompRef) =>
        pluginsServices.slots.isSlotContentPotentialMovable(compRef),
      plugins: {
        isPluginComponent: (widgetPluginCompRef: CompRef) =>
          pluginsServices.plugins.isPluginComponent(widgetPluginCompRef),
        showOnPage: (compRef: CompRef) =>
          pluginsServices.plugins.showOnPage(compRef),
        install: (
          slotComponentRef: ComponentRef,
          widgetRef: WidgetPointer,
          pluginsHostAPIContext: PluginsHostApiContext,
        ) =>
          pluginsServices.plugins.installWidgetPlugin(
            context,
            slotComponentRef,
            widgetRef,
            pluginsHostAPIContext,
          ),
        uninstall: (
          slotComponentRef: ComponentRef,
          pluginsHostAPIContext: PluginsHostApiContext,
        ) =>
          pluginsServices.plugins.uninstallWidgetPlugin(
            context,
            slotComponentRef,
            pluginsHostAPIContext,
          ),
      },
    },
    manifest: {
      behaviors: {
        is: {
          removable: (editorPointer: EditorPointer<CompRef>) =>
            manifestServices.behaviors.getValueFromManifest(
              editorPointer,
              'removable',
            ) ?? false,
          selectable: (editorPointer: EditorPointer<CompRef>) =>
            manifestServices.behaviors.getValueFromManifest(
              editorPointer,
              'selectable',
            ) ?? false,
          rotatable: (_editorPointer: EditorPointer<CompRef>) => false,
          duplicable: (_editorPointer: EditorPointer<CompRef>) => false,
          pinnable: (_editorPointer: EditorPointer<CompRef>) => false,
          anchorable: (_editorPointer: EditorPointer<CompRef>) => false,
        },
        get: {
          moveAxis: (_editorPointer: EditorPointer<CompRef>) => 'NONE',
          overflowOptions: (_editorPointer: EditorPointer<CompRef>) =>
            undefined,
          stretch: (_editorPointer: EditorPointer<CompRef>) => 'NONE',
        },
      },
      get: {
        displayName: (editorPointer: EditorPointer<CompRef>) =>
          manifestServices.get.displayName(editorPointer),
        style: (editorPointer: EditorPointer<CompRef>) =>
          manifestServices.get.style(editorPointer),
        presets: (editorPointer: EditorPointer<CompRef>) =>
          manifestServices.get.presets(editorPointer),
        data: (editorPointer: EditorPointer<CompRef>) =>
          manifestServices.get.data(editorPointer),
      },

      isBuilderComponentType: (compType: string) =>
        manifestServices.isBuilderComponentType(compType),
    },
    hierarchy: {
      getChildren: (
        editorPointer: EditorPointer<CompRef>,
        isRecursive: boolean,
      ) => manifestServices.getChildren(editorPointer, isRecursive),
      getAncestors: (editorPointer: EditorPointer<CompRef>) =>
        manifestServices.getAncestors(editorPointer),
    },
    panels: {
      getDesignPanel: (editorPointer: EditorPointer<CompRef>) =>
        panelsServices.getDesignPanel(editorPointer),
      addElements: {
        openIfExist: (compRef: CompRef) =>
          api.panels.addElements.openIfExist(documentServicesAPI, compRef),
      },
      addPanel: {
        getGroups: () =>
          api.panels.addPanel.getGroups(documentServicesAPI, context),
      },
    },
    editActions: {
      getActions: (editorPointer: EditorPointer<CompRef>, tags?: string[]) =>
        editActionsServices.generateEditActions(editorPointer, tags),
      contributeHostActions: (hostActions: HostEditActionsDictionary) =>
        api.editActions.contributeHostActions(hostActions),
      contributeExecutionMethods: (
        executionMethods: ExecutionMethodsDictionary,
      ) => api.editActions.contributeExecutionMethods(executionMethods),
    },
    reportBi,
  };
}
